import React from 'react';
import { motion } from 'framer-motion';
import { Spinner, Alert } from 'reactstrap';
import { RefreshCw, FileBarChart, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Loading state component
 */
const LoadingState = () => {
  const { t } = useTranslation();
  return (
    <div className="text-center py-5">
      <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
      <p className="text-muted mt-3">{t('shared.loading_data', 'Loading data...')}</p>
    </div>
  );
};

/**
 * Error state component
 */
const ErrorState = ({ message }) => {
  const { t } = useTranslation();
  return (
    <Alert color="danger" className="mb-0">
      <strong>{t('shared.error', 'Error:')}</strong> {message}
    </Alert>
  );
};

/**
 * Empty state component
 */
const EmptyState = ({ icon: Icon = FileBarChart, message }) => (
  <div className="text-center py-5 text-muted">
    <Icon size={48} className="mb-3" />
    <p className="mb-0">{message}</p>
  </div>
);

/**
 * ReportWidget - Base component for report graphs/widgets
 *
 * Provides consistent styling and state handling for all report widgets:
 * - Motion animation wrapper
 * - Loading, error, and empty states
 * - Optional header with icon and refresh button
 *
 * @param {string} title - Widget title
 * @param {React.Component} icon - Optional Lucide icon component
 * @param {boolean} loading - Loading state
 * @param {string} error - Error message
 * @param {boolean} isEmpty - Empty data state
 * @param {string} emptyMessage - Custom empty state message
 * @param {React.Component} emptyIcon - Custom icon for empty state
 * @param {function} onRefresh - Optional refresh callback
 * @param {function} onDetailsClick - Optional callback to navigate to report details
 * @param {React.ReactNode} children - Chart/content to render
 */
const ReportWidget = ({
  title,
  icon: Icon,
  loading = false,
  error = null,
  isEmpty = false,
  emptyMessage,
  emptyIcon,
  onRefresh,
  onDetailsClick,
  children
}) => {
  const { t } = useTranslation();
  const finalEmptyMessage = emptyMessage || t('shared.no_data_available', 'No data available for the selected period.');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card border-0 shadow-sm"
      style={{ borderRadius: '16px' }}
    >
      <div className="card-body p-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          {onDetailsClick ? (
            <h5
              className="fw-bold mb-0 text-primary"
              style={{ cursor: 'pointer' }}
              onClick={onDetailsClick}
            >
              {title}
              <svg width="8" height="12" viewBox="7 4 10 16" style={{ verticalAlign: 'middle', marginLeft: '6px', display: 'inline' }}>
                <polyline points="9 18 15 12 9 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </h5>
          ) : (
            <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
              {Icon && <Icon size={20} className="text-primary" />}
              {title}
            </h5>
          )}
          {onRefresh && (
            <button
              className="btn btn-sm btn-light d-flex align-items-center justify-content-center"
              onClick={onRefresh}
              disabled={loading}
              style={{ width: '32px', height: '32px', padding: 0 }}
            >
              <RefreshCw
                size={16}
                style={{
                  animation: loading ? 'spin 1s linear infinite' : 'none'
                }}
              />
            </button>
          )}
        </div>

        {/* Conditional States */}
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} />
        ) : isEmpty ? (
          <EmptyState icon={emptyIcon} message={finalEmptyMessage} />
        ) : (
          children
        )}
      </div>
    </motion.div>
  );
};

export default ReportWidget;
